import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSpring, useSprings, animated as a } from '@react-spring/web';
import Button from '../../buttons/Button';
import Detector from '../../partials/Detector';
import Html from '../../partials/Html';

import * as AppPropTypes from '../../../lib/PropTypes';

import styles from '../../../../styles/blocks/employee/card-stack.module.scss';

const propTypes = {
    className: PropTypes.string,
    isContainer: PropTypes.bool,
    title1: PropTypes.string,
    title2: PropTypes.string,
    cards: PropTypes.arrayOf(AppPropTypes.employeeCard),
};

const defaultProps = {
    className: null,
    isContainer: false,
    title1: null,
    title2: null,
    cards: null,
};

const CardStack = ({ className, isContainer, title1, title2, cards }) => {
    const [visible, setVisible] = useState(false);

    const [finalCards, setFinalCards] = useState([...cards].reverse());

    // container appear animation
    const [spring, setSpring] = useSpring(
        () => ({
            transform: `translateY(-20px)`,

            opacity: 0,
        }),
        [],
    );

    const [springs, setSprings] = useSprings(
        finalCards?.length,
        () => ({
            from: {
                y: 10,
            },
            to: {
                y: 0,
            },
        }),
        [finalCards],
    );

    const handleClick = useCallback(
        (card) => {
            if (card === finalCards[finalCards.length - 1]) {
                return;
            }
            const index = finalCards.indexOf(card);
            const newCards = [...finalCards.slice(index + 1), ...finalCards.slice(0, index + 1)];
            setFinalCards(newCards);
            setSprings({
                from: {
                    y: 10,
                    x: 10,
                },
                to: {
                    y: 0,
                    x: 0,
                },
                config: {
                    tension: 100,
                },
            });
        },
        [finalCards],
    );

    useEffect(() => {
        if (visible) {
            setSpring.start({
                transform: `translateY(0)`,

                opacity: 1,
            });
        } else {
            setSpring.start({
                transform: `translateY(-20px)`,

                opacity: 0,
            });
        }
    }, [visible]);

    return (
        <a.div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            style={{ ...spring }}
        >
            <Detector rootMargin="-30%" onChange={(v) => setVisible(v)}>
                <div
                    className={classNames([
                        styles.inner,
                        {
                            [styles.isContainer]: isContainer,
                        },
                    ])}
                >
                    <div className={styles.buttons}>
                        {(cards || []).map((card, i) => {
                            const { title = null, color = null } = card || {};
                            return (
                                <Button
                                    key={`button-card-stack-${i + 1}`}
                                    onClick={() => handleClick(card)}
                                    className={classNames([
                                        styles.button,
                                        {
                                            [styles.active]:
                                                card === finalCards[finalCards.length - 1],
                                        },
                                    ])}
                                    style={{
                                        backgroundColor: color !== null ? color : null,
                                    }}
                                >
                                    {title}
                                </Button>
                            );
                        })}
                    </div>

                    <div className={styles.cardsWrapper}>
                        <div className={styles.cards}>
                            {(springs || []).map((cardSpring, i) => {
                                const {
                                    title = null,
                                    text1 = null,
                                    text2 = null,
                                    image = null,
                                    color = null,
                                } = finalCards[i] || {};
                                const skipLastSpring = i !== 0 ? cardSpring : {};
                                return (
                                    <a.button
                                        key={`card-${i + 1}`}
                                        onClick={
                                            () =>
                                                handleClick(
                                                    finalCards[
                                                        i === 0 ? finalCards.length - 1 : i - 1
                                                    ],
                                                )
                                            // eslint-disable-next-line react/jsx-curly-newline
                                        }
                                        className={classNames([styles.card], {
                                            [styles.dropCard]: i !== cards.length - 1,
                                        })}
                                        style={{
                                            ...skipLastSpring,
                                            backgroundColor: color !== null ? color : null,
                                        }}
                                    >
                                        {i === cards.length - 1 ? (
                                            <>
                                                <div className={styles.cardInner}>
                                                    {title1 !== null ? (
                                                        <h3 className={styles.mobileTitle}>
                                                            {title1}
                                                        </h3>
                                                    ) : null}
                                                    <Html tag="p" body={text1} />
                                                </div>
                                                <div className={styles.cardInner}>
                                                    {title2 !== null ? (
                                                        <h3 className={styles.mobileTitle}>
                                                            {title2}
                                                        </h3>
                                                    ) : null}

                                                    <Html tag="p" body={text2} />
                                                </div>
                                                <div className={styles.iconContainer}>
                                                    <img
                                                        src={image}
                                                        alt={title}
                                                        className={styles.icon}
                                                    />
                                                </div>
                                            </>
                                        ) : null}
                                    </a.button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </Detector>
        </a.div>
    );
};

CardStack.propTypes = propTypes;
CardStack.defaultProps = defaultProps;

export default CardStack;
